import {
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  IconButton, MenuItem, Stack, Typography
} from '@mui/material';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { AgentSelector, Periodic } from '../../../features';
import { X } from '@phosphor-icons/react';
import { PeriodDateRange } from '../../../features/period-date-range';
import { SelectInput } from '@coverright/ui/inputs';
import { ActionItemFilterInput } from '@coverright/data-access/types/enrollment';

type Filters = Periodic<ActionItemFilterInput, 'dueDateRange'>

interface NextActionsFilterDrawerProps {
  open: boolean,
  onClose: (filter?: Filters) => void,
  initial: Filters
}

interface Props {
  open: (onCloseCallback: (filter: Filters) => void, filters: Filters) => void;
}

const NextActionsFilterDrawerContext = React.createContext<Props>({
  open: () => {}
});

export function withNextActionsFilterDrawer(WrappedComponent: React.ComponentType) {
  return () => {
    const [open, setOpen] = useState(false);
    const [filters, setFilters] = useState<Filters>({});
    const callback = useRef<(filter: Filters) => void>();

    const onClose: NextActionsFilterDrawerProps['onClose'] = filter => {
      setOpen(false);
      filter && callback.current && callback.current(filter);
    };

    const openDrawer = (onCloseCallback: (filter: Filters) => void, filter: Filters) => {
      setFilters(filter);
      callback.current = onCloseCallback;
      setOpen(true);
    };

    return <NextActionsFilterDrawerContext.Provider value={{ open: openDrawer }}>
      <NextActionsFilterDrawer open={open} onClose={onClose} initial={filters} />
      <WrappedComponent />
    </NextActionsFilterDrawerContext.Provider>;
  };
}

export const useNextActionsFilterDrawer = () => useContext(NextActionsFilterDrawerContext);

function NextActionsFilterDrawer({ open, onClose, initial }: NextActionsFilterDrawerProps) {
  const [filters, setFilters] = useState(initial || {});

  useEffect(() => {
    if (open) {
      setFilters(initial || {});
    } else {
      setFilters({});
    }
  }, [open, initial]);

  const setFilterValues = (values: Array<{ name: keyof ActionItemFilterInput, value: any }>) => {
    setFilters(prev => {
      const result = prev ? { ...prev } : {};
      for (const value of values) {
        result[value.name] = value.value === '-' ? undefined : value.value === undefined ? undefined : value.value;
      }
      return result;
    });
  };

  return <Drawer
    anchor={'right'}
    open={open}
    onClose={() => onClose()}
  >
    <Card sx={{ p: 4, width: 500, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant={'h3'}>2 Hour SLA filters</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size={'small'} onClick={() => onClose()}>
            <X />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 3, mt: 1 }} />
      <Stack component={'form'} onSubmit={(e: any) => e.preventDefault()} spacing={3}>
        <PeriodDateRange label={'Due date'} value={filters.dueDateRange || {}} onChange={(value) => {
          setFilterValues([{ name: 'dueDateRange', value }])
        }} />

        <AgentSelector showEmptyOption
                       value={filters.agentId}
                       onChange={value => setFilterValues([{ name: 'agentId', value }])} />

        <SelectInput label={'Is done'}
                     hideTick
                     value={typeof filters.done === 'boolean' ? filters.done.toString() : ''}
                     onChange={event => {
                       const value = event.target.value === '' ? undefined : event.target.value === 'true';
                       setFilterValues([{ name: 'done', value }])
                     }} >
          <MenuItem value={''}>-</MenuItem>
          <MenuItem value={'true'}>Yes</MenuItem>
          <MenuItem value={'false'}>No</MenuItem>
        </SelectInput>

        <div>
          <Button variant={'contained'} type={'submit'} onClick={() => onClose(filters)}>Search</Button>
        </div>
      </Stack>
    </Card>
  </Drawer>;
}
